// RBAC: izin = modül × eylem (görüntüle, düzenle, onayla, dışa aktar). 01-product-architecture.md.
import type { Module, TenantType } from './enums';

export const PERMISSION_MODULES = [
  'operations',
  'trips',
  'routes',
  'optimization',
  'requests',
  'passengers',
  'people',
  'fleet',
  'customers',
  'proposals',
  'announcements',
  'quality',
  'finance',
  'reports',
  'compliance',
  'devices',
  'integrations',
  'team',
  'billing',
  'settings',
  'audit',
  // platform
  'platform',
  'companies',
  'subscriptions',
  'support',
  'security',
  'privacy',
] as const;
export type PermissionModule = (typeof PERMISSION_MODULES)[number];

export const PERMISSION_MODULE_LABELS: Record<PermissionModule, string> = {
  operations: 'Canlı operasyon',
  trips: 'Seferler',
  routes: 'Rotalar',
  optimization: 'Optimizasyon',
  requests: 'Talepler',
  passengers: 'Yolcular',
  people: 'Şoför ve rehberler',
  fleet: 'Filo',
  customers: 'Müşteriler ve sözleşmeler',
  proposals: 'Teklifler',
  announcements: 'Duyurular',
  quality: 'Kalite',
  finance: 'Finans',
  reports: 'Raporlar',
  compliance: 'Uyum',
  devices: 'QR / NFC cihazları',
  integrations: 'Entegrasyonlar ve API',
  team: 'Ekip ve roller',
  billing: 'Plan ve kullanım',
  settings: 'Ayarlar',
  audit: 'Denetim günlüğü',
  platform: 'Platform',
  companies: 'Firmalar',
  subscriptions: 'Abonelikler',
  support: 'Destek',
  security: 'Güvenlik',
  privacy: 'KVKK',
};

export const ACTIONS = ['view', 'edit', 'approve', 'export'] as const;
export type Action = (typeof ACTIONS)[number];
export const ACTION_LABELS: Record<Action, string> = {
  view: 'Görüntüle',
  edit: 'Düzenle',
  approve: 'Onayla',
  export: 'Dışa aktar',
};

export type PermissionMatrix = Partial<Record<PermissionModule, Action[]>>;

const ALL: Action[] = ['view', 'edit', 'approve', 'export'];
const V: Action[] = ['view'];
const VE: Action[] = ['view', 'edit'];
const VX: Action[] = ['view', 'export'];
const VEA: Action[] = ['view', 'edit', 'approve'];

const COMPANY_MODULES: PermissionModule[] = [
  'operations',
  'trips',
  'routes',
  'optimization',
  'requests',
  'passengers',
  'people',
  'fleet',
  'customers',
  'proposals',
  'announcements',
  'quality',
  'finance',
  'reports',
  'compliance',
  'devices',
  'integrations',
  'team',
  'billing',
  'settings',
  'audit',
];

function full(mods: PermissionModule[]): PermissionMatrix {
  return Object.fromEntries(mods.map((m) => [m, ALL]));
}

export const SYSTEM_ROLES: Record<string, { name: string; tenantType: TenantType; permissions: PermissionMatrix }> = {
  OWNER: { name: 'Sahip', tenantType: 'SERVICE_COMPANY', permissions: full(COMPANY_MODULES) },
  OPERATIONS: {
    name: 'Operasyon',
    tenantType: 'SERVICE_COMPANY',
    permissions: {
      operations: VEA,
      trips: VEA,
      requests: VE,
      announcements: VE,
      quality: VE,
      passengers: V,
      people: V,
      fleet: V,
      routes: V,
      reports: VX,
      compliance: V,
      customers: V,
      devices: V,
    },
  },
  PLANNER: {
    name: 'Planlamacı',
    tenantType: 'SERVICE_COMPANY',
    permissions: {
      routes: ALL,
      optimization: ALL,
      requests: VEA,
      passengers: VE,
      proposals: ALL,
      trips: V,
      operations: V,
      customers: V,
      reports: VX,
      fleet: V,
    },
  },
  FLEET: {
    name: 'Filo',
    tenantType: 'SERVICE_COMPANY',
    permissions: { fleet: ALL, compliance: ALL, devices: VE, people: VE, trips: V, operations: V, reports: VX },
  },
  FINANCE: {
    name: 'Finans',
    tenantType: 'SERVICE_COMPANY',
    permissions: { finance: ALL, billing: ALL, reports: VX, customers: V, trips: V },
  },
  SUPPORT: {
    name: 'Destek',
    tenantType: 'SERVICE_COMPANY',
    permissions: { quality: VEA, requests: VE, passengers: VE, announcements: VE, trips: V, operations: V },
  },
  FIELD: { name: 'Saha', tenantType: 'SERVICE_COMPANY', permissions: { operations: V, trips: V, fleet: V, people: V } },
  INST_ADMIN: {
    name: 'Kurum yöneticisi',
    tenantType: 'INSTITUTION',
    permissions: {
      operations: V,
      trips: V,
      routes: V,
      requests: VEA,
      passengers: ALL,
      proposals: VEA,
      quality: VE,
      reports: VX,
      compliance: V,
      finance: VX,
      announcements: VE,
      team: VE,
      settings: VE,
    },
  },
  INST_VIEWER: {
    name: 'Kurum izleyici',
    tenantType: 'INSTITUTION',
    permissions: {
      operations: V,
      trips: V,
      routes: V,
      requests: V,
      passengers: V,
      quality: V,
      reports: VX,
      compliance: V,
      announcements: V,
    },
  },
  PLATFORM_ADMIN: {
    name: 'Platform yöneticisi',
    tenantType: 'PLATFORM',
    permissions: { ...full(['platform', 'companies', 'subscriptions', 'support', 'security', 'privacy', 'audit']) },
  },
  PLATFORM_SUPPORT: {
    name: 'Platform destek',
    tenantType: 'PLATFORM',
    permissions: { platform: V, companies: V, support: VEA, security: V, audit: V },
  },
  PLATFORM_FINANCE: {
    name: 'Platform finans',
    tenantType: 'PLATFORM',
    permissions: { platform: V, companies: V, subscriptions: ALL, audit: V },
  },
};

export function can(
  perms: PermissionMatrix | null | undefined,
  module: PermissionModule,
  action: Action = 'view',
): boolean {
  return Boolean(perms?.[module]?.includes(action));
}

/** Modül bayrağı → izin modülleri eşlemesi (bayrak kapalıysa bu izinler etkisizdir). */
export const FLAG_GATES: Partial<Record<PermissionModule, Module>> = {
  optimization: 'AI_OPTIMIZATION',
  finance: 'FINANCE',
};

export function effectivePermissions(
  perms: PermissionMatrix,
  flags: Partial<Record<Module, boolean>>,
): PermissionMatrix {
  const out: PermissionMatrix = {};
  for (const [m, actions] of Object.entries(perms) as [PermissionModule, Action[]][]) {
    const gate = FLAG_GATES[m];
    if (gate && !flags[gate]) continue;
    out[m] = actions;
  }
  return out;
}
